import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import { emptyInputs, compute, vaFundingTierText } from "@/lib/calculations";
import { fmt$ } from "@/lib/format";
import { api } from "@/lib/api";
import { useAnalysis } from "@/context/AnalysisContext";
import { track } from "@/lib/analytics";
import { ArrowRight, ArrowLeft, Plus, X, Info, Sparkles, Landmark, RotateCcw, ShieldCheck } from "lucide-react";
import { Link } from "react-router-dom";

const STEPS = ["Financing", "The Home", "Loan Details", "Financial Snapshot", "Closing Costs", "Pre-Closing"];

const specialtyOptions = [
  { key: "sewer_scope", label: "Sewer Scope", est: 175 },
  { key: "septic", label: "Septic Inspection", est: 400 },
  { key: "well", label: "Well Inspection", est: 250 },
  { key: "water_quality", label: "Water Quality Test", est: 150 },
  { key: "pest", label: "Pest / WDO", est: 125 },
  { key: "roof", label: "Roof Inspection", est: 250 },
  { key: "structural_engineer", label: "Structural Engineer", est: 550 },
  { key: "hvac", label: "HVAC Inspection", est: 200 },
  { key: "electrical", label: "Electrical Inspection", est: 200 },
  { key: "plumbing", label: "Plumbing Inspection", est: 200 },
  { key: "mold", label: "Mold Inspection", est: 400 },
  { key: "radon", label: "Radon Test", est: 175 },
];
const specialtyEstMap = Object.fromEntries(specialtyOptions.map(s => [s.key, s.est]));

const COMMON_SUMMARIES = {
  earnest_money: "Typically paid after your offer is accepted and usually expected to be applied as a credit toward amounts due at closing if the transaction closes. Contract terms vary.",
  general_inspection: "Usually paid during the inspection period and generally not credited toward closing.",
  appraisal: "Usually paid during loan processing. Your lender may collect this differently.",
};
const COMMON_TYPES = new Set(Object.keys(COMMON_SUMMARIES));
const INSPECTION_ESTIMATES = { general_inspection: { est: 550, range: "$450–$700" }, ...Object.fromEntries(specialtyOptions.map(s => [s.key, { est: s.est, range: null }])) };
// Types whose display name is a built-in Home Cost Path label and NOT user-editable.
const SYSTEM_TYPES = new Set([
  "earnest_money", "general_inspection", "appraisal",
  ...specialtyOptions.map(s => s.key),
]);

export default function Analyze() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { inputs, setInputs, currentStep: step, setCurrentStep: setStep, reset, savedPropertyId, hasWorksheet } = useAnalysis();
  const [vaLookup, setVaLookup] = useState({ state: null, fee: null, source: "", loading: false });
  const [taxLookup, setTaxLookup] = useState({ state: null, annual: null, rate: null, source: "", loading: false });
  const [showIntro, setShowIntro] = useState(() => {
    try { return !localStorage.getItem("hs_seen_intro") && !hasUserDataInline(); }
    catch (e) { console.warn("localStorage read failed", e); return true; }
  });

  // Deep-link support: /analyze?step=3. Always strip the param after mount so
  // refreshes restore the persisted currentStep, not the stale URL step.
  useEffect(() => {
    const raw = searchParams.get("step");
    if (raw != null) {
      const s = parseInt(raw, 10);
      if (!isNaN(s) && s >= 0 && s < STEPS.length && s !== step) setStep(s);
      const params = new URLSearchParams(searchParams);
      params.delete("step");
      setSearchParams(params, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reset scroll on wizard step change so mobile users always start at the top.
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, [step]);

  // Fire analysis_started once per wizard session when the user moves past the
  // financing-type step. No dollar values or PII are sent — just the coarse
  // financing_type category.
  const startedRef = React.useRef(false);
  useEffect(() => {
    if (!startedRef.current && step >= 1 && inputs.financing_type) {
      startedRef.current = true;
      track("analysis_started", { financing_type: inputs.financing_type });
    }
  }, [step, inputs.financing_type]);

  function hasUserDataInline() {
    // Same idea as context hasUserData, but read from context inputs snapshot at mount.
    const ks = ["purchase_price","interest_rate","monthly_income","cash_available"];
    return ks.some(k => inputs[k] != null && inputs[k] !== "" && Number(inputs[k]) !== 0);
  }

  const dismissIntro = () => {
    try { localStorage.setItem("hs_seen_intro", "1"); }
    catch (e) { console.warn("localStorage write failed", e); }
    setShowIntro(false);
  };
  const startZeroDown = () => {
    setInputs(p => ({
      ...p,
      financing_type: "va",
      purchase_price: "400000",
      interest_rate: "6.5",
      term_years: 30,
      down_payment: 0,
      down_payment_is_pct: false,
      va_exempt: "no",
      va_first_use: "first_use",
      annual_insurance: "1500",
      hoa_monthly: "0",
      closing_costs: { is_pct: true, value: 3 },
    }));
    dismissIntro();
    setStep(1);
  };

  const startNew = () => {
    if (hasWorksheet && !window.confirm("Start a new analysis? Your current unsaved changes will be cleared.")) return;
    reset();
    setStep(0);
    setShowIntro(true);
  };

  const set = (k, v) => setInputs(p => ({ ...p, [k]: v }));
  const setCC = (k, v) => setInputs(p => ({ ...p, closing_costs: { ...p.closing_costs, [k]: v } }));
  const setItem = (id, k, v) => setInputs(p => ({
    ...p, pre_closing_items: p.pre_closing_items.map(i => i.id === id ? { ...i, [k]: v } : i)
  }));
  const addItem = (label, type = "custom") => setInputs(p => ({
    ...p, pre_closing_items: [...p.pre_closing_items, {
      id: `it_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
      type, label, amount: "", paid_before_closing: true, refundable: "no", credited_at_closing: false, notes: "",
    }]
  }));
  const rmItem = (id) => setInputs(p => ({ ...p, pre_closing_items: p.pre_closing_items.filter(i => i.id !== id) }));

  // VA appraisal auto-lookup by ZIP (debounced)
  useEffect(() => {
    if (inputs.financing_type !== "va") { setVaLookup({ state: null, fee: null, source: "", loading: false }); return; }
    const z = String(inputs.zip || "").trim();
    if (z.length < 3) { setVaLookup({ state: null, fee: null, source: "", loading: false }); return; }
    let cancel = false;
    setVaLookup(v => ({ ...v, loading: true }));
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/lookup/va-appraisal?zip=${encodeURIComponent(z)}`);
        if (!cancel) setVaLookup({ state: data.state, fee: data.fee, source: data.source, loading: false });
      } catch { if (!cancel) setVaLookup({ state: null, fee: null, source: "", loading: false }); }
    }, 400);
    return () => { cancel = true; clearTimeout(t); };
  }, [inputs.zip, inputs.financing_type]);

  // Property tax auto-lookup by ZIP + price (debounced)
  useEffect(() => {
    const z = String(inputs.zip || "").trim();
    const p = Number(inputs.purchase_price || 0);
    if (z.length < 3 || !p) { setTaxLookup({ state: null, annual: null, rate: null, source: "", loading: false }); return; }
    let cancel = false;
    setTaxLookup(v => ({ ...v, loading: true }));
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/lookup/property-tax?zip=${encodeURIComponent(z)}&price=${p}`);
        if (!cancel) setTaxLookup({ state: data.state, annual: data.annual_estimate, rate: data.rate_pct, source: data.source, loading: false });
      } catch { if (!cancel) setTaxLookup({ state: null, annual: null, rate: null, source: "", loading: false }); }
    }, 400);
    return () => { cancel = true; clearTimeout(t); };
  }, [inputs.zip, inputs.purchase_price]);

  const applyTaxEstimate = () => {
    if (taxLookup.annual) {
      set("annual_taxes", String(taxLookup.annual));
      setInputs(p => ({ ...p, _estimated: { ...(p._estimated || {}), annual_taxes: true } }));
    }
  };
  const clearEstimateFlag = (key) => setInputs(p => ({ ...p, _estimated: { ...(p._estimated || {}), [key]: false } }));

  // Update the appraisal item label based on financing type
  useEffect(() => {
    setInputs(p => ({
      ...p,
      pre_closing_items: p.pre_closing_items.map(i =>
        i.type === "appraisal"
          ? { ...i, label: p.financing_type === "va" ? "VA Appraisal" : "Lender Appraisal" }
          : i
      ),
    }));
  }, [inputs.financing_type]);

  const earnestPresetPct = (pct) => {
    const price = Number(inputs.purchase_price || 0);
    return price ? Math.round(price * pct / 100) : 0;
  };
  const applyEarnestPreset = (amount) => {
    setInputs(p => ({
      ...p,
      pre_closing_items: p.pre_closing_items.map(i =>
        i.type === "earnest_money" ? { ...i, amount: String(amount) } : i
      ),
    }));
  };
  const applyVaAppraisalPreset = () => {
    if (!vaLookup.fee) return;
    setInputs(p => ({
      ...p,
      pre_closing_items: p.pre_closing_items.map(i =>
        i.type === "appraisal" ? { ...i, amount: String(vaLookup.fee) } : i
      ),
    }));
  };

  const next = () => setStep(s => Math.min(s + 1, STEPS.length - 1));
  const prev = () => setStep(s => Math.max(s - 1, 0));

  const submit = () => {
    const results = compute(inputs);
    // Keep sessionStorage for backwards-compat (Results also reads context).
    sessionStorage.setItem("hs_current", JSON.stringify({ inputs, results }));
    navigate("/results");
  };

  const isVA = inputs.financing_type === "va";
  const preview = compute(inputs);

  return (
    <>
      <Nav />
      <main className="container-hs py-10 md:py-14">
        <div className="max-w-4xl mx-auto">
          <div className="mb-4 flex items-center justify-between gap-3 flex-wrap">
            <div className="text-xs text-brand-muted">
              {savedPropertyId
                ? <span data-testid="editing-saved">Editing saved home · changes stay in this worksheet until you save</span>
                : hasWorksheet ? <span data-testid="worksheet-active">Your worksheet is autosaved locally</span> : null}
            </div>
            {hasWorksheet && (
              <button type="button" data-testid="start-new" onClick={startNew}
                className="text-xs text-brand-muted hover:text-brand-danger inline-flex items-center gap-1">
                <RotateCcw size={12} /> Start New Analysis
              </button>
            )}
          </div>
          <div className="mb-8">
            <div className="flex items-center justify-between text-xs text-brand-muted mb-2">
              <span data-testid="wizard-step-label">Step {step + 1} of {STEPS.length} — {STEPS[step]}</span>
              <span>{Math.round(((step + 1) / STEPS.length) * 100)}%</span>
            </div>
            <div className="h-1.5 rounded-full bg-brand-soft overflow-hidden">
              <div className="h-full bg-brand-accent transition-all duration-500" style={{ width: `${((step + 1) / STEPS.length) * 100}%` }} />
            </div>
          </div>

          <div className="card-hs p-6 sm:p-10">
            {step === 0 && showIntro && (
              <div data-testid="intro-zero-down" className="mb-6 rounded-2xl border border-brand-accent/40 bg-brand-soft/70 p-5">
                <div className="flex items-start gap-3">
                  <span className="mt-0.5 inline-flex h-8 w-8 items-center justify-center rounded-full bg-brand-accent text-white"><Sparkles size={16} /></span>
                  <div className="flex-1">
                    <div className="font-display text-lg text-brand-ink">First time here? Try a VA zero-down example.</div>
                    <p className="text-sm text-brand-muted mt-1">We'll pre-fill a common VA zero-down scenario ($400,000 purchase, 6.5% rate, first-use, first-time buyer). You can change anything as you go.</p>
                    <div className="mt-3 flex flex-wrap gap-2">
                      <button data-testid="intro-start" onClick={startZeroDown} className="btn-primary py-2 px-4 text-sm">Start with zero-down example <ArrowRight size={14}/></button>
                      <button data-testid="intro-dismiss" onClick={dismissIntro} className="btn-secondary py-2 px-4 text-sm">Not now, let me start blank</button>
                    </div>
                  </div>
                </div>
              </div>
            )}
            {step === 0 && (
              <div data-testid="wizard-step-0">
                <h2 className="font-display text-2xl sm:text-3xl text-brand-ink">How are you planning to finance this home?</h2>
                <p className="text-sm text-brand-muted mt-2">Select an option to continue. Version 1 has full support for VA loans; other programs are estimated.</p>
                <div className="grid sm:grid-cols-2 gap-3 mt-6">
                  {[
                    { v: "va", t: "VA Loan", d: "Full VA-specific calculations" },
                    { v: "conventional", t: "Conventional", d: "Basic payment & purchase-cost estimate available", badge: "Advanced loan rules coming soon" },
                    { v: "fha", t: "FHA", d: "Basic payment & purchase-cost estimate available", badge: "Advanced loan rules coming soon" },
                    { v: "usda", t: "USDA", d: "Basic payment & purchase-cost estimate available", badge: "Advanced loan rules coming soon" },
                    { v: "not_sure", t: "Not sure yet", d: "Get a general home-cost estimate without loan-program-specific fees." },
                  ].map(o => (
                    <button key={o.v} data-testid={`financing-${o.v}`}
                      onClick={() => set("financing_type", o.v)}
                      className={`text-left rounded-2xl border p-5 transition-colors ${
                        inputs.financing_type === o.v ? "border-brand-accent bg-brand-soft/50" : "border-brand-border hover:bg-brand-soft/40"
                      }`}>
                      <div className="font-medium text-brand-ink">{o.t}</div>
                      <div className="text-xs text-brand-muted mt-1">{o.d}</div>
                      {o.badge && <span className="badge-soft mt-3">{o.badge}</span>}
                    </button>
                  ))}
                </div>
                {isVA && (
                  <div data-testid="coe-reminder" className="mt-6 rounded-xl border border-brand-border bg-white p-4 flex items-start gap-3">
                    <span className="mt-0.5 inline-flex h-8 w-8 items-center justify-center rounded-full bg-brand-soft text-brand-accent shrink-0"><ShieldCheck size={16} /></span>
                    <div className="flex-1">
                      <div className="text-sm font-medium text-brand-ink">Have your Certificate of Eligibility (COE) ready?</div>
                      <p className="text-xs text-brand-muted mt-0.5 leading-relaxed">Most VA buyers request their COE through their lender. If you don't have one yet, we've put together a short checklist.</p>
                      <Link data-testid="coe-open" to="/coe" className="text-xs text-brand-accent hover:underline mt-1 inline-flex items-center gap-1">Open VA COE checklist <ArrowRight size={12} /></Link>
                    </div>
                  </div>
                )}
              </div>
            )}

            {step === 1 && (
              <div data-testid="wizard-step-1" className="space-y-5">
                <h2 className="font-display text-2xl sm:text-3xl text-brand-ink">Tell us about the home</h2>
                <div className="grid sm:grid-cols-2 gap-5">
                  <Field label="Purchase Price" testid="purchase-price" prefix="$" value={inputs.purchase_price} onChange={v => set("purchase_price", v)} />
                  <Field label="ZIP Code" testid="zip" value={inputs.zip} onChange={v => set("zip", v)} placeholder="12345" />
                  <div>
                    <label className="hs-label">Annual Property Taxes {inputs._estimated?.annual_taxes && <span className="badge-soft ml-2 text-[10px]">Estimated</span>}</label>
                    <div className="relative">
                      <span className="absolute left-4 top-1/2 -translate-y-1/2 text-brand-muted">$</span>
                      <input data-testid="annual-taxes" className="hs-input pl-8"
                        value={inputs.annual_taxes ?? ""}
                        onChange={e => { set("annual_taxes", e.target.value); clearEstimateFlag("annual_taxes"); }}
                        inputMode="decimal" />
                    </div>
                    <p className="hs-hint">Find this on most real-estate listings or leave blank for an estimate.</p>
                    {taxLookup.loading && <p className="hs-hint">Estimating from ZIP…</p>}
                    {!taxLookup.loading && taxLookup.annual && (
                      <div className="mt-2 flex flex-wrap items-center gap-2">
                        <button type="button" data-testid="tax-preset" onClick={applyTaxEstimate}
                          className="rounded-full border border-brand-accent bg-white px-3 py-1 text-xs text-brand-ink hover:bg-brand-soft">
                          <Landmark size={12} className="inline mr-1" />Use estimated {fmt$(taxLookup.annual)}/yr <span className="text-brand-muted">({taxLookup.state} · {taxLookup.rate}%)</span>
                        </button>
                        <span className="text-xs text-brand-muted">{taxLookup.source}</span>
                      </div>
                    )}
                  </div>
                  <div>
                    <label className="hs-label">Annual Homeowners Insurance</label>
                    <div className="relative">
                      <span className="absolute left-4 top-1/2 -translate-y-1/2 text-brand-muted">$</span>
                      <input data-testid="annual-insurance" className="hs-input pl-8" value={inputs.annual_insurance ?? ""} onChange={e => set("annual_insurance", e.target.value)} inputMode="decimal" />
                    </div>
                    <p className="hs-hint">Don't know yet? Leave blank and we'll use a clearly labeled planning estimate when available.</p>
                  </div>
                  <Field label="Monthly HOA Fee" testid="hoa" prefix="$" value={inputs.hoa_monthly} onChange={v => set("hoa_monthly", v)} />
                  <Field label="Other Monthly Housing (optional)" testid="other-housing" prefix="$" value={inputs.other_housing_monthly} onChange={v => set("other_housing_monthly", v)} />
                </div>
              </div>
            )}

            {step === 2 && (
              <div data-testid="wizard-step-2" className="space-y-5">
                <h2 className="font-display text-2xl sm:text-3xl text-brand-ink">Loan details</h2>
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className="hs-label">Loan Term</label>
                    <div className="flex gap-2">
                      {[30, 15].map(t => (
                        <button key={t} data-testid={`term-${t}`} onClick={() => set("term_years", t)}
                          className={`flex-1 rounded-xl border px-4 py-3 transition-colors ${inputs.term_years === t ? "border-brand-accent bg-brand-soft/60" : "border-brand-border"}`}>
                          {t} years
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <label className="hs-label">Interest Rate (%)</label>
                    <div className="relative">
                      <input data-testid="interest-rate" className="hs-input pr-10" value={inputs.interest_rate ?? ""} onChange={e => set("interest_rate", e.target.value)} placeholder="e.g. 6.5" inputMode="decimal" />
                      <span className="absolute right-4 top-1/2 -translate-y-1/2 text-brand-muted">%</span>
                    </div>
                    <p className="hs-hint">Use your lender's quoted rate. Don't have one yet? Enter a planning estimate.</p>
                  </div>
                  <div className="sm:col-span-2">
                    <label className="hs-label">Down Payment</label>
                    <div className="flex gap-2">
                      <input data-testid="down-payment" className="hs-input flex-1" value={inputs.down_payment} onChange={e => set("down_payment", e.target.value)} placeholder="0" />
                      <div className="flex rounded-xl border border-brand-border overflow-hidden">
                        <button data-testid="dp-toggle-usd" onClick={() => set("down_payment_is_pct", false)} className={`px-4 ${!inputs.down_payment_is_pct ? "bg-brand-accent text-white" : "bg-white text-brand-muted"}`}>$</button>
                        <button data-testid="dp-toggle-pct" onClick={() => set("down_payment_is_pct", true)} className={`px-4 ${inputs.down_payment_is_pct ? "bg-brand-accent text-white" : "bg-white text-brand-muted"}`}>%</button>
                      </div>
                    </div>
                    <p className="hs-hint">VA loans commonly allow $0 down for eligible buyers.</p>
                    {isVA && (
                      <div data-testid="va-tier-explanation" className="mt-2 rounded-xl bg-brand-bg border border-brand-border px-3 py-2 text-xs text-brand-ink">
                        {vaFundingTierText(inputs)}
                      </div>
                    )}
                  </div>
                </div>

                {isVA && (
                  <div className="rounded-2xl bg-brand-soft/50 p-5 space-y-4">
                    <div>
                      <div className="hs-label flex items-center gap-2">Are you exempt from the VA funding fee? <Tooltip text="Some eligible Veterans, service members, and surviving spouses may be exempt from the VA funding fee. Home Cost Path does not determine exemption eligibility. Confirm your status with your lender and VA documentation." /></div>
                      <div className="flex gap-2 flex-wrap">
                        {[["yes","Yes"],["no","No"],["not_sure","I'm not sure"]].map(([v,l]) => (
                          <button key={v} data-testid={`exempt-${v}`} onClick={() => set("va_exempt", v)}
                            className={`px-4 py-2 rounded-full border ${inputs.va_exempt === v ? "border-brand-accent bg-white" : "border-brand-border bg-white"}`}>{l}</button>
                        ))}
                      </div>
                      {inputs.va_exempt === "not_sure" && (
                        <p data-testid="exempt-not-sure-note" className="hs-hint mt-2 text-brand-warning">Planning estimate — exemption status not confirmed. Compare "if exempt" and "if not exempt" below.</p>
                      )}
                    </div>
                    <div>
                      <div className="hs-label">Is this your first use of your VA home-loan benefit?</div>
                      <div className="flex gap-2 flex-wrap">
                        {[["first_use","First use"],["subsequent_use","Subsequent use"],["not_sure","Not sure"]].map(([v,l]) => (
                          <button key={v} data-testid={`firstuse-${v}`} onClick={() => set("va_first_use", v)}
                            className={`px-4 py-2 rounded-full border ${inputs.va_first_use === v ? "border-brand-accent bg-white" : "border-brand-border bg-white"}`}>{l}</button>
                        ))}
                      </div>
                    </div>
                    <div className="grid sm:grid-cols-3 gap-3 pt-2">
                      <Mini k="Base loan amount" v={fmt$(preview.base_loan)} />
                      <Mini k={<span className="inline-flex items-center gap-1">Estimated VA funding fee <Tooltip text="The VA funding fee can often be financed into the loan rather than paid entirely in cash at closing. Some eligible borrowers are exempt. Your lender and VA determine the final fee and exemption status." /></span>} v={fmt$(preview.funding_fee)} />
                      <Mini k="Total financed" v={fmt$(preview.total_financed)} />
                    </div>
                    <div data-testid="va-funding-fee-note" className="rounded-xl border border-brand-border bg-white p-3 text-xs text-brand-muted leading-relaxed">
                      On a standard VA purchase loan, the VA funding fee may generally be financed into the loan amount. Other closing costs are typically paid at closing or covered through allowable seller credits, lender credits, or other permitted sources.
                    </div>
                    <div data-testid="va-funding-fee-source" className="text-[11px] text-brand-muted mt-1">
                      Source: 38 U.S.C. §3729; VA Home Loan funding-fee schedule effective April 7, 2023. Actual fee and exemption status are determined by VA and your lender.
                    </div>
                    <div data-testid="va-total-financed-note" className="rounded-xl border border-brand-border bg-white p-3 text-xs text-brand-muted leading-relaxed">
                      This financed amount may include the VA funding fee. Cash closing costs are tracked separately and are not automatically added to the VA purchase loan balance.
                    </div>
                    {inputs.va_exempt === "not_sure" && (() => {
                      // Show a side-by-side comparison without altering worksheet.
                      const exemptR = compute({ ...inputs, va_exempt: "yes" });
                      const nonR   = compute({ ...inputs, va_exempt: "no"  });
                      return (
                        <div data-testid="exempt-comparison" className="grid sm:grid-cols-2 gap-3">
                          <div className="rounded-xl bg-white p-4 border border-brand-border">
                            <div className="text-xs text-brand-muted">If exempt</div>
                            <div className="text-lg font-semibold text-brand-ink">Funding fee $0</div>
                            <div className="text-xs text-brand-muted mt-1">Total financed {fmt$(exemptR.total_financed)}</div>
                          </div>
                          <div className="rounded-xl bg-white p-4 border border-brand-border">
                            <div className="text-xs text-brand-muted">If not exempt</div>
                            <div className="text-lg font-semibold text-brand-ink">Funding fee {fmt$(nonR.funding_fee)}</div>
                            <div className="text-xs text-brand-muted mt-1">Total financed {fmt$(nonR.total_financed)}</div>
                          </div>
                        </div>
                      );
                    })()}
                    <p className="hs-hint">The VA funding fee is typically financed into your loan and is not added to the cash you bring to closing.</p>
                  </div>
                )}
              </div>
            )}

            {step === 3 && (
              <div data-testid="wizard-step-3" className="space-y-5">
                <h2 className="font-display text-2xl sm:text-3xl text-brand-ink">Your financial snapshot</h2>
                <p className="text-sm text-brand-muted">Optional: for a more useful affordability picture.</p>
                <div className="grid sm:grid-cols-2 gap-5">
                  <Field label="Gross Monthly Household Income" testid="income" prefix="$" value={inputs.monthly_income} onChange={v => set("monthly_income", v)} />
                  <Field label="Cash Available for This Home Purchase" testid="cash-available" prefix="$" value={inputs.cash_available} onChange={v => set("cash_available", v)}
                    hint="Enter the amount you would realistically be willing to use toward earnest money, inspections, appraisal, closing costs, down payment, and other purchase expenses." />
                  <Field label="Desired Cash Reserve After Closing (optional)" testid="desired-reserve" prefix="$" value={inputs.desired_reserve} onChange={v => set("desired_reserve", v)}
                    hint="How much money would you ideally like to keep untouched after buying the home?" />
                  <Field label="Monthly Car Payments" testid="car-payments" prefix="$" value={inputs.car_payments} onChange={v => set("car_payments", v)} />
                  <Field label="Min. Monthly Credit Card" testid="credit-card-min" prefix="$" value={inputs.credit_card_min} onChange={v => set("credit_card_min", v)} />
                  <Field label="Student Loan Payments" testid="student-loans" prefix="$" value={inputs.student_loans} onChange={v => set("student_loans", v)} />
                  <Field label="Personal Loan Payments" testid="personal-loans" prefix="$" value={inputs.personal_loans} onChange={v => set("personal_loans", v)} />
                  <Field label="Child Support / Alimony" testid="child-support" prefix="$" value={inputs.child_support} onChange={v => set("child_support", v)} />
                  <Field label="Other Monthly Debt" testid="other-debt" prefix="$" value={inputs.other_debt} onChange={v => set("other_debt", v)} />
                  <div className="sm:col-span-2">
                    <label className="hs-label">Estimated Credit Score Range (optional)</label>
                    <select data-testid="credit-range" className="hs-input" value={inputs.credit_score_range} onChange={e => set("credit_score_range", e.target.value)}>
                      <option value="">Select…</option>
                      {["Below 620","620–639","640–659","660–679","680–699","700–739","740+","Prefer not to answer"].map(o => <option key={o}>{o}</option>)}
                    </select>
                    {isVA && (
                      <p className="hs-hint">VA does not establish one universal minimum consumer credit score for all VA-guaranteed loans. Individual lenders may apply their own credit standards.</p>
                    )}
                  </div>
                </div>
                <p className="hs-hint">Lenders may calculate income and debt differently. This estimate is for planning only.</p>
              </div>
            )}

            {step === 4 && (
              <div data-testid="wizard-step-4" className="space-y-5">
                <h2 className="font-display text-2xl sm:text-3xl text-brand-ink">Estimated closing costs</h2>
                <div className="rounded-2xl bg-brand-soft/50 p-5">
                  <label className="hs-label">Do you have a lender Loan Estimate?</label>
                  <div className="flex gap-2 flex-wrap mt-1">
                    {[["yes","Yes — I'll enter my actual estimate"],["no","No — help me estimate"]].map(([v,l]) => (
                      <button key={v} type="button" data-testid={`lender-estimate-${v}`}
                        onClick={() => set("has_lender_estimate", v)}
                        className={`px-4 py-2 rounded-full border text-sm ${inputs.has_lender_estimate === v ? "border-brand-accent bg-white text-brand-ink" : "border-brand-border bg-white text-brand-muted hover:border-brand-accent"}`}>
                        {l}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="hs-label">Estimated Closing Costs</label>
                  <p className="hs-hint mb-2">This can include lender, title, settlement, recording, and other transaction charges. If you separately enter prepaid taxes, insurance, escrow deposits, or discount points, do not include those amounts twice.</p>
                  {(inputs.closing_costs.mode || "simple") === "simple" ? (
                    <>
                      <div className="flex gap-2">
                        <input data-testid="cc-value" className="hs-input flex-1" value={inputs.closing_costs.value} onChange={e => setCC("value", e.target.value)} inputMode="decimal" />
                        <div className="flex rounded-xl border border-brand-border overflow-hidden">
                          <button type="button" data-testid="cc-toggle-usd" onClick={() => setCC("is_pct", false)} className={`px-4 ${!inputs.closing_costs.is_pct ? "bg-brand-accent text-white" : "bg-white text-brand-muted"}`}>$</button>
                          <button type="button" data-testid="cc-toggle-pct" onClick={() => setCC("is_pct", true)} className={`px-4 ${inputs.closing_costs.is_pct ? "bg-brand-accent text-white" : "bg-white text-brand-muted"}`}>%</button>
                        </div>
                      </div>
                      <p className="hs-hint mt-2" data-testid="cc-mode-simple">Using simple closing-cost estimate · ~{fmt$(preview.closing_costs)}</p>
                      {inputs.has_lender_estimate === "no" && (
                        <div className="mt-3 flex flex-wrap gap-2 items-center">
                          <span className="text-xs text-brand-muted mr-1">Planning presets:</span>
                          {[2, 3, 4].map(pct => (
                            <button key={pct} type="button" data-testid={`cc-preset-${pct}`}
                              onClick={() => { setCC("is_pct", true); setCC("value", pct); }}
                              className={`rounded-full border px-3 py-1 text-xs ${inputs.closing_costs.is_pct && Number(inputs.closing_costs.value) === pct ? "border-brand-accent bg-white text-brand-ink" : "border-brand-border bg-white text-brand-muted hover:border-brand-accent"}`}>
                              {pct}%
                            </button>
                          ))}
                          <button type="button" data-testid="cc-preset-custom"
                            onClick={() => { setCC("is_pct", true); }}
                            className="rounded-full border border-brand-border bg-white px-3 py-1 text-xs text-brand-muted hover:border-brand-accent">
                            Custom
                          </button>
                          <span className="text-xs text-brand-muted w-full mt-1">Planning estimates only — actual closing costs vary by lender, location, loan structure, taxes, insurance, title charges, and other factors.</span>
                        </div>
                      )}
                      <button type="button" data-testid="cc-switch-detailed"
                        onClick={() => setCC("mode", "detailed")}
                        className="text-xs text-brand-accent hover:underline mt-3">
                        Break down closing costs into categories →
                      </button>
                    </>
                  ) : (
                    <>
                      <div className="grid sm:grid-cols-2 gap-3">
                        {[
                          ["lender", "Lender fees"],
                          ["title", "Title / Settlement / Escrow"],
                          ["recording", "Recording fees"],
                          ["other", "Other eligible closing charge"],
                        ].map(([k, label]) => (
                          <div key={k}>
                            <label className="hs-label">{label}</label>
                            <div className="relative">
                              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-brand-muted">$</span>
                              <input data-testid={`cc-detailed-${k}`} className="hs-input pl-8"
                                value={inputs.closing_costs.detailed?.[k] ?? ""}
                                onChange={e => setInputs(p => ({ ...p, closing_costs: { ...p.closing_costs, detailed: { ...(p.closing_costs.detailed || {}), [k]: e.target.value } } }))}
                                inputMode="decimal" />
                            </div>
                          </div>
                        ))}
                      </div>
                      <p className="hs-hint mt-2" data-testid="cc-mode-detailed">Using detailed closing-cost breakdown · sum: {fmt$(preview.closing_costs)}</p>
                      <button type="button" data-testid="cc-switch-simple"
                        onClick={() => setCC("mode", "simple")}
                        className="text-xs text-brand-accent hover:underline mt-2">
                        ← Use a simple total instead
                      </button>
                    </>
                  )}
                </div>
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className="hs-label">Seller Contribution Toward Closing Costs</label>
                    <div className="relative">
                      <span className="absolute left-4 top-1/2 -translate-y-1/2 text-brand-muted">$</span>
                      <input data-testid="seller-credit" className="hs-input pl-8" value={inputs.seller_credit ?? ""} onChange={e => set("seller_credit", e.target.value)} inputMode="decimal" />
                    </div>
                    <p className="hs-hint">Enter the total amount you expect to ask the seller to contribute toward allowable closing-related costs.</p>
                  </div>
                  <div>
                    <label className="hs-label">Lender Credit</label>
                    <div className="relative">
                      <span className="absolute left-4 top-1/2 -translate-y-1/2 text-brand-muted">$</span>
                      <input data-testid="lender-credit" className="hs-input pl-8" value={inputs.lender_credit ?? ""} onChange={e => set("lender_credit", e.target.value)} inputMode="decimal" />
                    </div>
                    <p className="hs-hint">A lender credit is money provided by the lender toward eligible closing costs, often in exchange for a different interest rate or loan structure.</p>
                  </div>
                  <Field label="Other Credit (optional)" prefix="$" value={inputs.other_credit} onChange={v => set("other_credit", v)} />
                </div>

                <details className="rounded-2xl border border-brand-border bg-white p-4">
                  <summary data-testid="cc-advanced" className="cursor-pointer text-sm font-medium text-brand-ink select-none">Advanced closing-cost detail (optional)</summary>
                  <p className="text-xs text-brand-muted mt-2">Add other at-closing items that are typically listed separately from the closing-cost total. These are added on top of the closing-cost total above.</p>
                  <div className="grid sm:grid-cols-2 gap-4 mt-3">
                    <Field label="Prepaid Taxes / Insurance / Escrow" prefix="$" value={inputs.prepaids} onChange={v => set("prepaids", v)} testid="prepaids" />
                    <Field label="Discount Points / Rate Buydown" prefix="$" value={inputs.discount_points} onChange={v => set("discount_points", v)} />
                    <Field label="Other Closing Expense" prefix="$" value={inputs.other_closing} onChange={v => set("other_closing", v)} />
                  </div>
                </details>
                {isVA && (
                  <>
                    <div data-testid="va-seller-credit-note" className="rounded-xl border border-brand-border bg-brand-soft/40 p-4 text-xs text-brand-muted leading-relaxed">
                      <b className="text-brand-ink">Note for VA loans:</b> VA rules distinguish ordinary seller-paid closing-cost credits from seller concessions. Ordinary allowable closing-cost credits are treated differently from concessions, while certain seller concessions are subject to limits. Home Cost Path provides a planning estimate only; confirm allowable seller-paid amounts with your lender.
                    </div>
                    {Number(inputs.seller_credit || 0) > 0.04 * Number(inputs.purchase_price || 0) && Number(inputs.purchase_price || 0) > 0 && (
                      <div data-testid="large-seller-flag" className="rounded-xl border border-brand-warning/40 bg-brand-warning/10 p-3 text-xs text-brand-ink">
                        <b>Large seller contribution</b> — confirm allowable treatment with your VA lender.
                      </div>
                    )}
                  </>
                )}
                <div className="rounded-2xl bg-brand-soft/50 p-5 grid sm:grid-cols-3 gap-3">
                  <Mini k="Estimated closing costs" v={fmt$(preview.closing_costs)} />
                  <Mini k="Total credits" v={fmt$(preview.total_credits)} />
                  <Mini k="Estimated cash at closing" v={fmt$(preview.additional_cash_at_closing)} />
                </div>
              </div>
            )}

            {step === 5 && (
              <div data-testid="wizard-step-5" className="space-y-5">
                <h2 className="font-display text-2xl sm:text-3xl text-brand-ink">Money you may need before closing</h2>
                <p className="text-sm text-brand-muted">Your down payment isn't the only money you may need. Some expenses occur days or weeks before closing — even with a zero-down loan.</p>
                <div className="space-y-3">
                  {inputs.pre_closing_items.map(item => {
                    const isEarnest = item.type === "earnest_money";
                    const isAppraisal = item.type === "appraisal";
                    const isCommon = COMMON_TYPES.has(item.type);
                    const isSystemName = SYSTEM_TYPES.has(item.type);
                    const inspectionEst = INSPECTION_ESTIMATES[item.type];
                    const detailsOpen = !!item._details_open || !isCommon;
                    return (
                    <div key={item.id} data-testid={`pre-item-${item.id}`} className="rounded-2xl border border-brand-border p-4">
                      <div className="flex items-start justify-between gap-3">
                        {isSystemName ? (
                          <div data-testid={`item-name-${item.id}`} className="font-medium text-brand-ink py-2">{item.label}</div>
                        ) : (
                          <input data-testid={`item-name-${item.id}`} className="hs-input font-medium max-w-md" value={item.label} onChange={e => setItem(item.id, "label", e.target.value)} />
                        )}
                        <button onClick={() => rmItem(item.id)} className="p-2 text-brand-muted hover:text-brand-danger" aria-label="Remove"><X size={16} /></button>
                      </div>

                      {isCommon && (
                        <p className="text-xs text-brand-muted mt-2 leading-relaxed">{COMMON_SUMMARIES[item.type]}</p>
                      )}

                      {isEarnest && (
                        <div className="mt-3 rounded-xl bg-brand-soft/50 p-3">
                          <div className="text-xs font-medium text-brand-ink mb-2">Planning estimates</div>
                          <div className="flex flex-wrap gap-2">
                            {[1, 2, 3].map(pct => {
                              const amt = earnestPresetPct(pct);
                              const hasPrice = amt > 0;
                              return (
                                <button key={pct} type="button" data-testid={`earnest-preset-${pct}`}
                                  disabled={!hasPrice}
                                  onClick={() => hasPrice && applyEarnestPreset(amt)}
                                  className={`rounded-full border px-3 py-1.5 text-sm transition-colors ${
                                    hasPrice && Number(item.amount) === amt
                                      ? "border-brand-accent bg-white text-brand-ink"
                                      : "border-brand-border bg-white text-brand-muted hover:border-brand-accent disabled:opacity-60 disabled:cursor-not-allowed"
                                  }`}>
                                  {pct}%{hasPrice ? ` — ${fmt$(amt)}` : ""}
                                </button>
                              );
                            })}
                            <span className="text-xs text-brand-muted self-center ml-1">
                              {earnestPresetPct(1) > 0 ? "or enter custom below" : "Enter Purchase Price on Step 2 to see dollar values."}
                            </span>
                          </div>
                          <p className="hs-hint mt-2">Earnest money requirements vary by local market and purchase contract. Home Cost Path does not determine the amount required for your offer.</p>
                        </div>
                      )}

                      {inspectionEst && (
                        <div className="mt-3 flex flex-wrap items-center gap-2">
                          {(() => {
                            const selected = Number(item.amount) === inspectionEst.est;
                            return (
                              <>
                                <button type="button" data-testid={`inspection-preset-${item.id}`}
                                  onClick={() => setItem(item.id, "amount", String(inspectionEst.est))}
                                  className={`rounded-full border px-3 py-1 text-xs transition-colors ${
                                    selected
                                      ? "border-brand-accent bg-brand-accent/10 text-brand-ink"
                                      : "border-dashed border-brand-border bg-white/60 text-brand-muted hover:border-brand-accent hover:bg-brand-soft"
                                  }`}>
                                  {selected ? `Using ${fmt$(inspectionEst.est)} estimate ✓` : `Use ${fmt$(inspectionEst.est)} estimate`}
                                </button>
                                <span className="text-xs text-brand-muted">
                                  {selected
                                    ? <>Planning estimate selected. Replace it with your actual amount when known.</>
                                    : <>Planning suggestion{inspectionEst.range ? ` · typical range ${inspectionEst.range}` : ""} — not included until selected or an amount is entered.</>
                                  }
                                </span>
                              </>
                            );
                          })()}
                        </div>
                      )}

                      {isAppraisal && inputs.financing_type === "va" && (
                        <div className="mt-3 rounded-xl bg-brand-soft/50 p-3 space-y-2">
                          <div className="text-xs">
                            <b className="text-brand-ink">VA appraisal ≠ home inspection.</b>
                            <span className="text-brand-muted"> The VA appraisal evaluates value and applicable property requirements. A separate independent home inspection can help evaluate the home's physical condition in greater detail.</span>
                          </div>
                          {vaLookup.loading && <div className="text-xs text-brand-muted">Looking up VA appraisal fee…</div>}
                          {!vaLookup.loading && vaLookup.fee && (
                            <div className="flex flex-wrap items-center gap-3">
                              {(() => {
                                const selected = Number(item.amount) === vaLookup.fee;
                                return (
                                  <>
                                    <button type="button" data-testid="va-appraisal-preset" onClick={applyVaAppraisalPreset}
                                      className={`rounded-full border px-3 py-1.5 text-sm transition-colors ${
                                        selected
                                          ? "border-brand-accent bg-brand-accent/10 text-brand-ink"
                                          : "border-dashed border-brand-border bg-white/60 text-brand-muted hover:border-brand-accent hover:bg-brand-soft"
                                      }`}>
                                      {selected ? <>Using {fmt$(vaLookup.fee)} estimate ✓ <span className="text-brand-muted">({vaLookup.state})</span></>
                                                : <>Use estimated {fmt$(vaLookup.fee)} <span className="text-brand-muted">({vaLookup.state})</span></>}
                                    </button>
                                    <span className="text-xs text-brand-muted">
                                      {selected
                                        ? <>Planning estimate selected. Replace it with your lender's amount when known.</>
                                        : <>{vaLookup.source} Not included until selected or an amount is entered.</>
                                      }
                                    </span>
                                  </>
                                );
                              })()}
                            </div>
                          )}
                          {!vaLookup.loading && !vaLookup.fee && (
                            <p className="text-xs text-brand-muted">
                              {inputs.zip ? "We couldn't reliably map this ZIP to a VA appraisal fee. " : "Enter a ZIP code on Step 2 for an automatic estimate. "}
                              Please enter the appraisal amount provided by your lender.
                            </p>
                          )}
                        </div>
                      )}
                      {isAppraisal && inputs.financing_type !== "va" && (
                        <p className="hs-hint mt-2">Editable estimate. Use the figure provided by your lender when available.</p>
                      )}

                      <div className="mt-3 grid sm:grid-cols-2 gap-3">
                        <Field label="Amount" prefix="$" value={item.amount} onChange={v => setItem(item.id, "amount", v)} testid={`${item.id}-amount`} />
                        {isCommon && !detailsOpen && (
                          <div className="flex items-end">
                            <button type="button" data-testid={`change-details-${item.id}`}
                              onClick={() => setItem(item.id, "_details_open", true)}
                              className="text-xs text-brand-accent hover:underline">
                              Change details (paid before / refundable / credited)
                            </button>
                          </div>
                        )}
                      </div>
                      {detailsOpen && (
                        <div className="grid sm:grid-cols-3 gap-3 mt-3">
                          <Choice label="Paid before closing?" value={item.paid_before_closing ? "yes" : "no"} onChange={v => setItem(item.id, "paid_before_closing", v === "yes")} opts={[["yes","Yes"],["no","No"]]} />
                          <Choice label="Refundable?" value={item.refundable} onChange={v => setItem(item.id, "refundable", v)} opts={[["yes","Yes"],["no","No"],["not_sure","Not sure"]]} />
                          <Choice label="Credited at closing?" value={item.credited_at_closing ? "yes" : "no"} onChange={v => setItem(item.id, "credited_at_closing", v === "yes")} opts={[["yes","Yes"],["no","No"]]} />
                        </div>
                      )}
                    </div>
                    );
                  })}
                </div>

                <div className="pt-2">
                  <div className="text-sm font-medium text-brand-ink mb-2">Add specialty inspection</div>
                  <div className="flex flex-wrap gap-2">
                    {specialtyOptions.map(s => (
                      <button key={s.key} data-testid={`add-${s.key}`} onClick={() => addItem(s.label, s.key)}
                        className="rounded-full border border-brand-border bg-white px-3 py-1.5 text-sm text-brand-muted hover:bg-brand-soft/50">
                        <Plus size={12} className="inline mr-1" />{s.label}
                      </button>
                    ))}
                  </div>
                </div>
                <button data-testid="add-custom" onClick={() => addItem("Custom Expense", "custom")}
                  className="btn-secondary text-sm py-2 px-4 mt-2"><Plus size={14} /> Add Custom Expense</button>

                <div className="rounded-2xl bg-brand-soft/50 p-5 grid sm:grid-cols-3 gap-3 mt-4">
                  <Mini k="Cash before closing" v={fmt$(preview.cash_before_closing)} />
                  <Mini k="Cash at closing" v={fmt$(preview.additional_cash_at_closing)} />
                  <Mini k="Max transaction cash" v={fmt$(preview.max_transaction_cash)} accent />
                </div>
              </div>
            )}

            <div className="flex justify-between items-center pt-8 mt-6 border-t border-brand-border">
              <button data-testid="wizard-back" onClick={prev} disabled={step === 0} className="btn-secondary disabled:opacity-40">
                <ArrowLeft size={16} /> Back
              </button>
              {step < STEPS.length - 1 ? (
                <button data-testid="wizard-next" onClick={next} className="btn-primary">Continue <ArrowRight size={16} /></button>
              ) : (
                <button data-testid="wizard-submit" onClick={submit} className="btn-primary">See Results <ArrowRight size={16} /></button>
              )}
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}

function Field({ label, value, onChange, prefix, suffix, hint, testid, placeholder }) {
  return (
    <div>
      <label className="hs-label">{label}</label>
      <div className="relative">
        {prefix && <span className="absolute left-4 top-1/2 -translate-y-1/2 text-brand-muted">{prefix}</span>}
        <input data-testid={testid} className={`hs-input ${prefix ? "pl-8" : ""} ${suffix ? "pr-10" : ""}`}
          value={value ?? ""} onChange={e => onChange(e.target.value)} placeholder={placeholder} inputMode="decimal" />
        {suffix && <span className="absolute right-4 top-1/2 -translate-y-1/2 text-brand-muted">{suffix}</span>}
      </div>
      {hint && <p className="hs-hint">{hint}</p>}
    </div>
  );
}
function Mini({ k, v, accent }) {
  return <div className="rounded-xl bg-white p-4 border border-brand-border">
    <div className="text-xs text-brand-muted">{k}</div>
    <div className={`text-lg font-semibold mt-1 ${accent ? "text-brand-accent" : "text-brand-ink"}`}>{v}</div>
  </div>;
}
function Tooltip({ text }) {
  return (
    <span className="relative inline-flex group">
      <Info size={14} className="text-brand-muted cursor-help" />
      <span className="pointer-events-none absolute left-1/2 -translate-x-1/2 top-full mt-2 z-30 w-64 rounded-xl bg-white border border-brand-border p-3 text-xs text-brand-muted shadow-lg opacity-0 group-hover:opacity-100 transition-opacity">
        {text}
      </span>
    </span>
  );
}
function Choice({ label, value, onChange, opts }) {
  return <div>
    <label className="hs-label">{label}</label>
    <select className="hs-input" value={value} onChange={e => onChange(e.target.value)}>
      {opts.map(([v,l]) => <option key={v} value={v}>{l}</option>)}
    </select>
  </div>;
}
